import { useState } from "react";
import { Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { ConfirmButton } from "../../components/common/ConfirmButton";
import CrudDialog from "../../components/common/CrudDialog";
import { SERVICE_TYPE } from "../../lib/labels";

export default function Services() {
  const { can } = useCompany();
  const { data, loading, error, reload } = useApi("/company/services");
  const agencies = useApi("/company/agencies");
  const team = useApi("/company/team");
  const [editing, setEditing] = useState(null);
  const [agencyFilter, setAgencyFilter] = useState("");
  const manage = can("structure:update");
  const canCreate = can("structure:create");
  const canDelete = can("structure:delete");
  const agencyName = (id) => agencies.data?.find((a) => a.id === id)?.name;
  const memberName = (id) => team.data?.find((m) => m.user_id === id)?.name;
  const teamOpts = (team.data || []).map((m) => [m.user_id, m.name]);
  const fields = [
    { name: "name", label: "Nom du service", required: true, half: true },
    { name: "type", label: "Type d'activité", type: "select", half: true, options: Object.entries(SERVICE_TYPE) },
    { name: "agency_id", label: "Agence de rattachement", type: "select", nullable: true, half: true,
      options: [["", "Toutes les agences / transversal"], ...(agencies.data || []).map((a) => [a.id, a.name])] },
    { name: "status", label: "Statut", type: "select", half: true, options: [["active", "Actif"], ["inactive", "Désactivé"]] },
    { name: "description", label: "Description" },
    { name: "manager_id", label: "Responsable", type: "select", nullable: true, options: [["", "Aucun"], ...teamOpts] },
    { name: "member_ids", label: "Collaborateurs affectés", type: "multi", options: teamOpts },
  ];
  const bodyOf = (s) => ({ name: s.name, type: s.type || "autre", description: s.description || "", agency_id: s.agency_id || null,
    manager_id: s.manager_id || null, member_ids: s.member_ids || [], status: s.status || "active" });
  const toggle = async (s) => {
    const next = s.status === "inactive" ? "active" : "inactive";
    try { await api.put(`/company/services/${s.id}`, { ...bodyOf(s), status: next }); toast.success(next === "active" ? "Service activé" : "Service désactivé"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rows = (data || []).filter((s) => !agencyFilter || s.agency_id === agencyFilter);

  const remove = async (s) => {
    try { await api.delete(`/company/services/${s.id}`); toast.success("Service supprimé"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Services" description="Pôles, départements ou prestations sur lesquels les usagers peuvent s'exprimer."
        actions={<>
          <select className="field-select w-48" value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)} data-testid="service-agency-filter">
            <option value="">Toutes les agences</option>{(agencies.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {canCreate && <Button onClick={() => setEditing({ name: "", type: "accueil", description: "", agency_id: "", manager_id: "", member_ids: [], status: "active" })} className="rounded-full" data-testid="service-create-button"><Plus className="mr-1.5 h-4 w-4" />Nouveau service</Button>}
        </>} />
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={Layers} title="Aucun service" text="Créez vos services pour orienter les feedbacks vers les bonnes équipes." />}
      {data && data.length > 0 && (
        <div className="card-surface divide-y">
          {rows.map((s) => (
            <div key={s.id} className={`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center ${s.status === "inactive" ? "opacity-60" : ""}`} data-testid={`service-row-${s.id}`}>
              <div className="flex-1">
                <p className="font-medium">{s.name} <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-normal">{SERVICE_TYPE[s.type] || "Autre"}</span>
                  {s.status === "inactive" && <span className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-normal" data-testid={`service-inactive-${s.id}`}>Désactivé</span>}</p>
                <p className="text-sm text-slate-500">{agencyName(s.agency_id) || "Transversal"}{s.description ? ` · ${s.description}` : ""}</p>
                <p className="text-xs text-slate-500">Responsable : {memberName(s.manager_id) || "—"} · Équipe : {(s.member_ids || []).map(memberName).filter(Boolean).join(", ") || "—"}</p>
              </div>
              {manage && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => toggle(s)} data-testid={`service-toggle-${s.id}`}>{s.status === "inactive" ? "Activer" : "Désactiver"}</Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Modifier</Button>
                  {canDelete && <ConfirmButton testId={`service-delete-${s.id}`} title="Supprimer ce service ?" description="Impossible si des feedbacks y sont liés." confirmLabel="Supprimer" onConfirm={() => remove(s)}
                    trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`service-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>} />}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {editing && (
        <CrudDialog open onOpenChange={(o) => { if (!o) { setEditing(null); } }} testId="service" fields={fields}
          title={editing.id ? "Modifier le service" : "Nouveau service (un QR code sera généré)"} initial={{ ...bodyOf(editing), agency_id: editing.agency_id || "", manager_id: editing.manager_id || "" }}
          endpoint={editing.id ? `/company/services/${editing.id}` : "/company/services"} method={editing.id ? "put" : "post"} onSaved={() => reload()} />
      )}
    </div>
  );
}
